import { useMemo, useState } from 'react';
import type { Diary, Farmer } from '../../../../types';
import { getFieldsByFarmer, getLogsByFarmer, getSeasonsByFarmer, getEditHistories, getMaterialsByFarmer, addLog } from '../../../../store';
import { LogFormModal } from '../../../farmer/log/components/LogFormModal';
import { useAuth } from '../../../../hooks/useAuth';
import { showToast } from '../../../../components/base/Toast';
import { EmptyState } from '../../../../components/base/EmptyState';
import { MobileTabs } from '../../../../components/base/MobileUi';
import { LogDetailModal } from '../../../farmer/log/components/LogDetailModal';
import { formatDate } from '../../../../utils/format';

interface DetailDiaryTabProps {
  farmer: Farmer;
  /** Lets the "chưa có thửa đất" hint jump to the Mùa vụ tab, where creating a
   * season also creates the field the diary form needs. */
  onNavigateTab?: (tab: 'production') => void;
}

type PeriodMode = 'day' | 'week' | 'month';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Monday-based week start, matching how Vietnamese calendars/work-weeks are read. */
function startOfWeek(d: Date): Date {
  const result = new Date(d);
  const day = result.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + diff);
  return result;
}

function periodRange(mode: PeriodMode, anchor: Date): { start: Date; end: Date } {
  if (mode === 'day') return { start: anchor, end: anchor };
  if (mode === 'week') {
    const start = startOfWeek(anchor);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  }
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { start, end };
}

function shiftAnchor(mode: PeriodMode, anchor: Date, direction: 1 | -1): Date {
  const next = new Date(anchor);
  if (mode === 'day') next.setDate(next.getDate() + direction);
  else if (mode === 'week') next.setDate(next.getDate() + direction * 7);
  else next.setMonth(next.getMonth() + direction);
  return next;
}

function periodLabel(mode: PeriodMode, start: Date, end: Date): string {
  if (mode === 'day') return formatDate(toISODate(start));
  if (mode === 'week') return `${formatDate(toISODate(start))} – ${formatDate(toISODate(end))}`;
  return `Tháng ${start.getMonth() + 1}/${start.getFullYear()}`;
}

/** Full "Nhật ký" tab — promoted out of DetailProductionTab.tsx's old
 * 5-entry "Nhật ký gần đây" embed (Feedback round 4: "thêm phần nhật ký...
 * trong profile nông hộ", matching how Tài chính already gets its own full
 * tab instead of a capped summary). Feedback round 7: replaced the free-form
 * date-range pair with Day/Week/Month period navigation plus season/crop
 * filters, and previews each entry instead of dumping full text inline. */
export default function DetailDiaryTab({ farmer, onNavigateTab }: DetailDiaryTabProps) {
  const { user } = useAuth();
  // HTX keeps the diary on the farmer's behalf — in particular for a profile it
  // created before the farmer has an account. Once that profile is synced to
  // the farmer's own account, the farmer writes their own diary.
  const isSyncLocked = !!farmer.createdWithoutAccount && !!farmer.userId;
  const canWriteDiary = user?.role === 'Hợp tác xã' && farmer.membershipStatus === 'Đã xác nhận' && !isSyncLocked;
  const [formOpen, setFormOpen] = useState(false);
  const fields = getFieldsByFarmer(farmer.id);
  const seasons = getSeasonsByFarmer(farmer.id);
  const allDiaries = getLogsByFarmer(farmer.id).filter((d) => !d.isDeleted);
  const [onlyWarnings, setOnlyWarnings] = useState(false);
  const [seasonId, setSeasonId] = useState('');
  const [crop, setCrop] = useState('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('week');
  const [anchor, setAnchor] = useState(() => {
    // Default to the most recent entry's period rather than today's — a
    // farmer who last logged three weeks ago should not open to an empty week.
    const latest = allDiaries.reduce((max, d) => (d.ngay > max ? d.ngay : max), '');
    return latest ? parseISODate(latest) : new Date();
  });
  const [detailItem, setDetailItem] = useState<Diary | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const getFieldName = (id?: string) => fields.find((f) => f.id === id)?.name || '-';
  const getSeason = (id?: string) => seasons.find((s) => s.id === id);

  const cropOptions = useMemo(() => Array.from(new Set(seasons.map((s) => s.cayTrong).filter(Boolean))), [seasons]);

  const { start, end } = useMemo(() => periodRange(periodMode, anchor), [periodMode, anchor]);
  const startStr = toISODate(start);
  const endStr = toISODate(end);

  const categoryFiltered = useMemo(() => {
    return allDiaries
      .filter((d) => (!onlyWarnings || (d.warningLevel && d.warningLevel !== 'none')))
      .filter((d) => !seasonId || d.seasonId === seasonId)
      .filter((d) => !crop || getSeason(d.seasonId)?.cayTrong === crop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDiaries, onlyWarnings, seasonId, crop, seasons]);

  const filtered = useMemo(() => {
    return categoryFiltered
      .filter((d) => d.ngay >= startStr && d.ngay <= endStr)
      .sort((a, b) => new Date(b.ngay).getTime() - new Date(a.ngay).getTime());
  }, [categoryFiltered, startStr, endStr]);

  const warningCount = allDiaries.filter((d) => d.warningLevel && d.warningLevel !== 'none').length;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = (data: Omit<Diary, 'id'>) => {
    addLog({ ...data, farmerId: farmer.id, editCount: 0 })
      .then(() => showToast('Đã ghi nhật ký cho nông hộ', 'success'))
      .catch((e) => showToast(e instanceof Error ? e.message : 'Ghi nhật ký thất bại', 'error'));
  };

  return (
    <div className="space-y-4">
      {canWriteDiary && (
        fields.length > 0 ? (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="w-full rounded-xl bg-agrida-600 py-2.5 text-sm font-semibold text-white"
          >
            + Ghi nhật ký cho nông hộ
          </button>
        ) : (
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-800">
            Nông hộ chưa có thửa đất nên chưa ghi nhật ký được.{' '}
            <button type="button" onClick={() => onNavigateTab?.('production')} className="font-semibold underline">
              Tạo mùa vụ
            </button>{' '}
            (tạo luôn thửa đất) trước.
          </div>
        )
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">Tổng nhật ký</p>
          <p className="text-lg font-bold text-slate-800">{allDiaries.length}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">Có cảnh báo</p>
          <p className="text-lg font-bold text-amber-700">{warningCount}</p>
        </div>
      </div>

      {/* Season / crop filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className="rounded-xl border border-slate-200 px-2.5 py-2 text-xs bg-white">
          <option value="">Tất cả mùa vụ</option>
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={crop} onChange={(e) => setCrop(e.target.value)} className="rounded-xl border border-slate-200 px-2.5 py-2 text-xs bg-white">
          <option value="">Tất cả cây trồng</option>
          {cropOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
          <input type="checkbox" checked={onlyWarnings} onChange={(e) => setOnlyWarnings(e.target.checked)} className="rounded border-slate-300" />
          Chỉ cảnh báo
        </label>
      </div>

      {/* Period mode + prev/next navigation */}
      <div className="flex items-center gap-2">
        <MobileTabs
          tabs={[{ id: 'day', label: 'Ngày' }, { id: 'week', label: 'Tuần' }, { id: 'month', label: 'Tháng' }]}
          value={periodMode}
          onChange={(mode) => { setPeriodMode(mode); setAnchor(new Date()); }}
          equal
        />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-2 py-1.5">
        <button type="button" onClick={() => setAnchor((a) => shiftAnchor(periodMode, a, -1))} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <span className="text-xs font-semibold text-slate-700">{periodLabel(periodMode, start, end)}</span>
        <button type="button" onClick={() => setAnchor((a) => shiftAnchor(periodMode, a, 1))} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="ri-book-open-line" title="Chưa có nhật ký" description="Không có nhật ký phù hợp với bộ lọc trong khoảng thời gian này." />
      ) : (
        <div className="space-y-2">
          {filtered.map((d) => {
            const expanded = expandedIds.has(d.id);
            const isLong = d.chiTiet && d.chiTiet.length > 90;
            return (
              <div key={d.id} className={`bg-white rounded-xl border p-3 ${d.warningLevel && d.warningLevel !== 'none' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-xs font-semibold text-slate-900">{formatDate(d.ngay)}</p>
                  <span className="text-xs text-slate-400">{getFieldName(d.fieldId)}</span>
                  {d.warningTags && d.warningTags.length > 0 && d.warningTags.map((tag) => (
                    <span key={tag} className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">{tag}</span>
                  ))}
                </div>
                <p className="text-sm text-slate-700">{d.hoatDong}</p>
                <p className={`text-xs text-slate-500 mt-0.5 ${expanded ? '' : 'line-clamp-2'}`}>{d.chiTiet}</p>
                {d.ghiChuHTX && (
                  <div className="mt-1.5 flex items-start gap-1.5">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-agrida-100 text-agrida-700 shrink-0">Ghi chú HTX</span>
                    <span className="text-xs text-slate-600">{d.ghiChuHTX}</span>
                  </div>
                )}
                <div className="mt-1.5 flex items-center gap-3">
                  {isLong && (
                    <button type="button" onClick={() => toggleExpanded(d.id)} className="text-xs font-medium text-agrida-700">
                      {expanded ? 'Thu gọn' : 'Xem thêm'}
                    </button>
                  )}
                  <button type="button" onClick={() => setDetailItem(d)} className="text-xs font-medium text-slate-500 hover:text-slate-700">
                    Chi tiết đầy đủ →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <LogFormModal
          fields={fields}
          seasons={seasons}
          farmerId={farmer.id}
          farmerMaterials={getMaterialsByFarmer(farmer.id)}
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
      )}

      {detailItem && (
        <LogDetailModal
          log={detailItem}
          field={fields.find((f) => f.id === detailItem.fieldId)}
          season={getSeason(detailItem.seasonId)}
          histories={getEditHistories().filter((h) => h.entityId === detailItem.id && h.entityType === 'Diary')}
          onClose={() => setDetailItem(null)}
        />
      )}
    </div>
  );
}
